import { CheckCheck } from "lucide-react";
import { useState } from "react";

import { NotificationList } from "@/features/notifications/components/NotificationList";
import type { Notification } from "@/features/notifications/lib/sentences";
import { useT } from "@/shared/i18n/useT";
import { Button } from "@/shared/ui/Button";
import { SegmentedControl } from "@/shared/ui/SegmentedControl";

type Filter = "all" | "unread";

interface NotificationPanelProps {
  unreadCount: number;
  onOpen: (notification: Notification) => void;
  onReadAll: () => void;
  markingAll: boolean;
  /** Show the "Notifications" heading (the sheet has its own header). */
  showTitle?: boolean;
}

/** The inbox: All or Unread, mark everything read, open one to go to its task. */
export function NotificationPanel({
  unreadCount,
  onOpen,
  onReadAll,
  markingAll,
  showTitle = true,
}: NotificationPanelProps) {
  const t = useT();
  const [filter, setFilter] = useState<Filter>("all");

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-col gap-3 border-b border-line px-4 pt-4 pb-3">
        {showTitle ? (
          <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-ink">
            {t("notifications.title")}
          </h2>
        ) : null}
        <div className="flex items-center justify-between gap-3">
          <SegmentedControl
            label={t("notifications.filterLabel")}
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: t("notifications.filter.all") },
              { value: "unread", label: t("notifications.filter.unread"), count: unreadCount },
            ]}
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={unreadCount === 0 || markingAll}
            onClick={onReadAll}
          >
            <CheckCheck aria-hidden="true" />
            {t("notifications.markAll")}
          </Button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <NotificationList unreadOnly={filter === "unread"} onOpen={onOpen} />
      </div>
    </div>
  );
}
